import api from "../../../../../features/auth/axiosConfig";
import { notifyError, notifySuccess } from "../../../../../assets/toastService";
import { useState } from "react";

export const useBranchUpdate = (
  id: string,
  formData: any,
  originalData: any,
  closeEdit: () => void,
  fetchBranches: () => void,
  dateDirty: boolean,
) => {
  const [loading, setLoading] = useState(false);

  const buildPayload = () => {
    const payload: any = {};

    // 1. Handle basic fields
    const basicFields = ["name"];
    basicFields.forEach((key) => {
      if (formData[key] !== undefined && formData[key] !== originalData?.[key]) {
        payload[key] = formData[key];
      }
    });

    // 2. Handle openingDate (with dateDirty check)
    if (formData.openingDate && dateDirty) {
      const formattedDate = formatDate(formData.openingDate);
      const originalDate = formatDate(originalData?.openingDate);

      if (formattedDate !== originalDate) {
        payload.openingDate = formattedDate;
      }
    }

    // 3. Handle address fields - they should be nested under 'addresses'
    const addressChanged = checkAddressChanges(formData, originalData);
    if (addressChanged) {
      payload.addresses = {
        addressLine1: formData.addressLine1 || "",
        addressLine2: formData.addressLine2 || "",
        country: formData.country || "",
        state: formData.state || "",
        city: formData.city || "",
        postalCode: formData.postalCode || "",
      };
    }

    return payload;
  };

  const formatDate = (dateObj: any): string | null => {
    if (!dateObj) return null;

    // Handle MongoDB extended JSON format
    if (dateObj.$date) {
      return dateObj.$date;
    }

    // Handle Date object
    if (dateObj instanceof Date) {
      return dateObj.toISOString();
    }

    // Handle string
    if (typeof dateObj === "string") {
      return dateObj;
    }

    return null;
  };

  const checkAddressChanges = (formData: any, originalData: any): boolean => {
    const addressFields = ["addressLine1", "addressLine2", "country", "state", "city", "postalCode"];

    for (const field of addressFields) {
      const newValue = formData[field] || "";
      const oldValue = originalData?.addresses?.[field] || "";

      if (newValue !== oldValue) {
        return true;
      }
    }

    return false;
  };

  const updateBranch = async () => {
    const payload = buildPayload();

    if (Object.keys(payload).length === 0) {
      notifyError("No changes detected");
      return;
    }

    try {
      setLoading(true);

      await api.patch(`/app/pos/v2/branch/${id}`, payload);
      notifySuccess("Branch updated successfully");
      closeEdit();
      fetchBranches();
    } catch (err: any) {
      console.error("❌ Update error:", err.response?.data);

      // Handle specific backend errors
      if (err.response?.data?.message === "No valid fields to update") {
        notifyError("No valid changes detected. Please modify at least one field.");
      } else {
        notifyError(err.response?.data?.message || "Failed to update branch");
      }
    } finally {
      setLoading(false);
    }
  };

  return { loading, updateBranch };
};
